import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, getCsrfCookie } from '../api/client';

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  type: 'customer' | 'staff';
  phone: string | null;
  company: string | null;
  language: string;
  timezone: string;
  country: string | null;
  has_2fa_enabled: boolean;
  email_verified: boolean;
  is_super_admin?: boolean;
  permissions: string[];
  groups: string[];
}

export interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
  turnstile_token?: string;
}

export interface LoginResponse {
  message: string;
  user: {
    id: number;
    email: string;
    name: string;
    type: 'customer' | 'staff';
    requires_device_verification: boolean;
    requires_2fa: boolean;
    has_2fa_enabled: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isInitializing: boolean;
  isAuthenticated: boolean;
  isStaff: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasGroup: (group: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<{ user: User }>('/auth/user');
      setUser(response.user);
    } catch {
      setUser(null);
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Ensure we have CSRF cookie before login
    await getCsrfCookie();

    const response = await api.post<LoginResponse>('/auth/login', credentials);

    // Refresh user data after successful login
    await refreshUser();

    return response;
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    if (user.groups.includes('super-admin')) return true;
    return user.permissions.includes(permission);
  }, [user]);

  const hasGroup = useCallback((group: string): boolean => {
    if (!user) return false;
    return user.groups.includes(group);
  }, [user]);

  // Check auth status on mount (skip on guest-only routes to avoid 401 noise)
  useEffect(() => {
    const guestRoutes = ['/login', '/forgot-password', '/reset-password', '/setup', '/wordpress-signup'];
    const isGuestRoute = guestRoutes.some(route => window.location.pathname.startsWith(route));

    const checkAuth = async () => {
      try {
        await getCsrfCookie();
        // Skip auth check on guest routes - user is likely not authenticated
        if (!isGuestRoute) {
          await refreshUser();
        }
      } catch {
        // API not available or request failed - continue without auth
        setUser(null);
      } finally {
        setIsInitializing(false);
      }
    };

    checkAuth();
  }, [refreshUser]);

  // Listen for auth events
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
    };

    const handleCsrfMismatch = () => {
      // Reload page to get new CSRF token
      window.location.reload();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:csrf-mismatch', handleCsrfMismatch);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:csrf-mismatch', handleCsrfMismatch);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isLoading: false, // Pages handle their own loading states
    isInitializing,
    isAuthenticated: !!user,
    isStaff: user?.type === 'staff',
    login,
    logout,
    refreshUser,
    hasPermission,
    hasGroup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hook for checking staff build
export function isStaffBuild(): boolean {
  return typeof __IS_STAFF_BUILD__ !== 'undefined' && __IS_STAFF_BUILD__;
}

// Type declaration for the global variable
declare global {
  const __IS_STAFF_BUILD__: boolean;
}
